import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Loader2, Layers } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { PageContainer } from "../components/kit";

// Which FEED is supplying the jobs candidates actually see, and how much of it is duplicated.
//
// Everything here is a copy of jobs already fetched for real candidates — no job API is called to
// produce it. The number that matters is EXCLUSIVE: jobs only that feed carried. A feed with high
// volume and low exclusivity is paying to repeat what another feed already gave you.
const COLORS = { jobo: "#0025D8", ats_board: "#0FA968", adzuna: "#E08A00", jsearch: "#7C3AED",
                 careerjet: "#DB2777", external: "#64748B" };
const color = (s) => COLORS[s] || "#64748B";

function Bars({ series, sources }) {
  if (!series.length) return null;
  const max = Math.max(1, ...series.map((d) => Math.max(...sources.map((s) => d[s.source] || 0))));
  return (
    <div className="overflow-x-auto">
      <div className="flex items-end gap-1 h-24 min-w-fit">
        {series.map((d) => (
          <div key={d.day} className="flex flex-col items-center gap-1 flex-shrink-0" title={d.day}>
            <div className="flex items-end gap-0.5 h-20">
              {sources.map((s) => (
                <div key={s.source} style={{ background: color(s.source),
                       height: `${Math.round(((d[s.source] || 0) / max) * 100)}%`, width: 6 }}
                  className="rounded-sm min-h-[2px]" title={`${s.source}: ${d[s.source] || 0}`} />
              ))}
            </div>
            <span className="text-[9px] text-muted-foreground font-mono">{d.day.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminJobSupply() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/job-supply?days=${days}`)
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Could not load job supply"))
      .finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const sources = data?.sources || [];
  return (
    <PageContainer width="wide">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0F172A] mb-4">
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F172A]">Auto-apply job supply</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-[70ch]">
            Only the jobs shown to candidates on the Autopilot Jobs grid — where they came from and
            which ATSes they sit on. Recorded from what each feed already returned; nothing is fetched
            to build this page.
          </p>
          {data && (
            <div className="flex items-center gap-4 mt-3">
              <span className="text-[13px] text-[#0F172A]">
                <b className="text-lg font-bold tabular-nums">{data.total_unique}</b> jobs shown
              </span>
              <span className="text-[13px] text-[#0F172A]">
                <b className="text-lg font-bold tabular-nums">{data.total_appliable}</b> ⚡ auto-appliable
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-[#0F172A]">
            <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
          </select>
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" title="Refresh">
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      {loading && !data && <div className="flex items-center gap-2 text-sm text-muted-foreground py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

      {data && !sources.length && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <Layers className="w-6 h-6 text-slate-300 mx-auto mb-2" />
          <div className="text-sm font-semibold text-[#0F172A]">Nothing recorded yet</div>
          <p className="text-[12.5px] text-muted-foreground mt-1 max-w-[46ch] mx-auto leading-relaxed">
            Rows appear as candidates load their matches or their ready-to-apply list.
            Nothing is fetched to fill this page.
          </p>
        </div>
      )}

      {!!sources.length && (
        <>
          <div className="rounded-xl border border-border bg-white overflow-hidden mb-5">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-secondary/40 border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                  <th className="px-3 py-2.5 font-medium">Source</th>
                  <th className="px-3 py-2.5 font-medium">Unique jobs</th>
                  <th className="px-3 py-2.5 font-medium">Exclusive</th>
                  <th className="px-3 py-2.5 font-medium">⚡ appliable</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sources.map((s) => (
                  <tr key={s.source}>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-2 font-semibold text-[#0F172A]">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color(s.source) }} />
                        {s.source}
                      </span>
                    </td>
                    <td className="px-3 py-3 tabular-nums font-semibold">{s.unique}</td>
                    <td className="px-3 py-3 tabular-nums">
                      <span className="font-semibold">{s.exclusive}</span>
                      <span className="text-muted-foreground text-[11px] ml-1">
                        ({s.unique ? Math.round((s.exclusive / s.unique) * 100) : 0}%)
                      </span>
                    </td>
                    <td className="px-3 py-3 tabular-nums">{s.appliable}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">ATS shown to candidates</div>
              {!(data.ats || []).length ? (
                <p className="text-[12.5px] text-muted-foreground">Nothing recorded yet.</p>
              ) : (
                <div className="space-y-1">
                  {data.ats.map((a) => (
                    <div key={a.ats} className="flex items-center gap-2 text-[12.5px]">
                      <span className="font-semibold text-[#0F172A]">{a.ats}</span>
                      <span className="ml-auto tabular-nums font-semibold">{a.jobs}</span>
                      <span className="text-muted-foreground text-[11px] w-20 text-right">
                        {a.appliable ? `${a.appliable} ⚡` : "no ⚡"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Overlap between feeds</div>
              {!(data.overlap || []).length ? (
                <p className="text-[12.5px] text-muted-foreground">
                  No job has been supplied by more than one source yet — every source is adding jobs the others didn't have.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {data.overlap.map((o) => (
                    <div key={o.a + o.b} className="flex items-center gap-2 text-[12.5px]">
                      <span className="font-semibold text-[#0F172A]">{o.a}</span>
                      <span className="text-muted-foreground">∩</span>
                      <span className="font-semibold text-[#0F172A]">{o.b}</span>
                      <span className="ml-auto tabular-nums font-semibold">{o.jobs}</span>
                      <span className="text-muted-foreground text-[11px]">shared</span>
                    </div>
                  ))}
                  <p className="text-[11px] text-muted-foreground pt-1.5 border-t border-border mt-2">
                    {data.total_unique} distinct jobs in total across all sources.
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Daily unique jobs</div>
              <Bars series={data.series || []} sources={sources} />
              <div className="flex flex-wrap gap-3 mt-3">
                {sources.map((s) => (
                  <span key={s.source} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color(s.source) }} />{s.source}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </PageContainer>
  );
}
